'use client';
import React, {
  useState, useRef, useCallback, useEffect, createContext, useContext,
} from 'react';
import { flushSync } from 'react-dom';
import Link from 'next/link';
import dynamic from 'next/dynamic';
import { useRouter, usePathname } from 'next/navigation';
import { useSession } from 'next-auth/react';
import { format } from 'date-fns';
import type { Block, PostMeta, PostAuthor } from './types';

// Mirrors post-index.ts — generic team names hidden from public byline
const _INNO_TYPO_RE = /^(Team\s+)?[Ii]?n{1,3}ovac{1,2}e?r{1,2}$/i;
const _BRAND_TEAM_RE = /^Team\s+(gravity|flow|comet|galaxy|atlas|story\s*health)$/i;
const _EXTRA_GENERIC = new Set(['team innvoaccer', 'team innovacecr']);
function isGenericAuthor(name: string): boolean {
  const n = name.trim();
  return _INNO_TYPO_RE.test(n) || _BRAND_TEAM_RE.test(n) || _EXTRA_GENERIC.has(n.toLowerCase());
}
import { BLOCK_GROUPS, BLOCK_LABELS, defaultBlock } from './types';
import { BlockRenderer, FormatToolbar, sanitizePastedHtml } from './BlockRenderer';
import { BlockPickerPopup } from './BlockPickerPopup';
import { NavBarMain } from '@/components/seasons/NavBarMain';
import { FlowNavBar } from '@/components/seasons/FlowNavBar';
import { CometNavBar } from '@/components/seasons/CometNavBar';
import { GalaxyNavBar } from '@/components/seasons/GalaxyNavBar';
import { GravityNavBar } from '@/components/seasons/GravityNavBar';
import { StoryhealthNavBar } from '@/components/seasons/StoryhealthNavBar';
import { InputSubmit } from '@/components/seasons/InputSubmit';
import { BlogListenButtonAuto } from '@/components/blog-sections/BlogListenButtonAuto';
import { BlogShareInline } from '@/components/seasons/BlogShareInline';
import { toEditorial, toCategorySlug, NEWSROOM_CATEGORY } from '@/lib/editorial-categories';
import { CalendarBlank } from '@phosphor-icons/react';
import { brandExternalUrl, adminBasePathForBrand } from '@/lib/brand-urls';

import { FrontmatterPanel } from './FrontmatterPanel';
import { PublishToggle } from './PublishToggle';
const Footer = dynamic(
  () => import('@/components/seasons/Footer').then(m => ({ default: m.Footer })),
  { ssr: false },
);
const FlowFooter = dynamic(
  () => import('@/components/seasons/FlowFooter').then(m => ({ default: m.FlowFooter })),
  { ssr: false },
);
const CometFooter = dynamic(
  () => import('@/components/seasons/CometFooter').then(m => ({ default: m.CometFooter })),
  { ssr: false },
);
const GalaxyFooter = dynamic(
  () => import('@/components/seasons/GalaxyFooter').then(m => ({ default: m.GalaxyFooter })),
  { ssr: false },
);
const StoryhealthFooter = dynamic(
  () => import('@/components/seasons/StoryhealthFooter').then(m => ({ default: m.StoryhealthFooter })),
  { ssr: false },
);
const GravityFooter = dynamic(
  () => import('@/components/seasons/GravityFooter').then(m => ({ default: m.GravityFooter })),
  { ssr: false },
);

/* ═══════════════════════════════════════════════════════════
   Table of contents (mirrors client-page.tsx BlogTOC)
   Only rendered in preview mode where headings are stable
   ═══════════════════════════════════════════════════════════ */
function BlogTOC({ articleId }: { articleId: string }) {
  const [headings, setHeadings] = useState<{ id: string; text: string; level: number }[]>([]);
  const [activeId, setActiveId] = useState('');
  const [sheetOpen, setSheetOpen] = useState(false);

  useEffect(() => {
    const article = document.getElementById(articleId);
    if (!article) return;
    const els = Array.from(article.querySelectorAll('h2,h3')) as HTMLElement[];
    els.forEach((el, i) => { if (!el.id) el.id = `heading-${i}`; });
    setHeadings(els.map(el => ({ id: el.id, text: el.textContent || '', level: parseInt(el.tagName[1]) })));
    const obs = new IntersectionObserver(
      (entries) => { entries.forEach(e => { if (e.isIntersecting) setActiveId(e.target.id); }); },
      { rootMargin: '-10% 0% -80% 0%', threshold: 0 },
    );
    els.forEach(el => obs.observe(el));
    return () => obs.disconnect();
  }, [articleId]);

  useEffect(() => {
    if (!sheetOpen) return;
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') setSheetOpen(false); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [sheetOpen]);

  if (headings.length < 2) return null;

  const scrollTo = (id: string) => {
    document.getElementById(id)?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    setSheetOpen(false);
  };

  const tocItems = headings.map(h => (
    <li key={h.id} className="blog-toc__item">
      <a
        href={`#${h.id}`}
        className={`blog-toc__link${h.level === 3 ? ' blog-toc__link--h3' : ''}${activeId === h.id ? ' is-active' : ''}`}
        aria-current={activeId === h.id ? 'location' : undefined}
        onClick={(e) => { e.preventDefault(); scrollTo(h.id); }}
      >{h.text}</a>
    </li>
  ));

  return (
    <>
      <button type="button" className="blog-toc-pill" aria-label="Open table of contents" aria-expanded={sheetOpen} onClick={() => setSheetOpen(true)}>
        <svg width="16" height="16" fill="currentColor" viewBox="0 0 256 256" aria-hidden="true"><path d="M224,128a8,8,0,0,1-8,8H40a8,8,0,0,1,0-16H216A8,8,0,0,1,224,128ZM40,72H216a8,8,0,0,0,0-16H40a8,8,0,0,0,0,16ZM216,184H40a8,8,0,0,0,0,16H216a8,8,0,0,0,0-16Z" /></svg>
        <span>Contents</span>
      </button>
      {sheetOpen && <div className="blog-toc-sheet-backdrop" aria-hidden="true" onClick={() => setSheetOpen(false)} />}
      <div className={`blog-toc-sheet${sheetOpen ? ' blog-toc-sheet--open' : ''}`} role="dialog" aria-modal="true" aria-label="In this article">
        <div className="blog-toc-sheet__header">
          <span className="blog-toc__label">In this article</span>
          <button type="button" className="btn-icon-only btn-icon-only--sm btn-icon-only--outline" aria-label="Close table of contents" onClick={() => setSheetOpen(false)}>
            <svg width="16" height="16" fill="currentColor" viewBox="0 0 256 256" aria-hidden="true"><path d="M205.66,194.34a8,8,0,0,1-11.32,11.32L128,139.31,61.66,205.66a8,8,0,0,1-11.32-11.32L116.69,128,50.34,61.66A8,8,0,0,1,61.66,50.34L128,116.69l66.34-66.35a8,8,0,0,1,11.32,11.32L139.31,128Z" /></svg>
          </button>
        </div>
        <ul className="blog-toc__list">{tocItems}</ul>
      </div>
    </>
  );
}

/* ═══════════════════════════════════════════════════════════
   Editor context — shared state for nested components
   ═══════════════════════════════════════════════════════════ */
export interface EditorCtx {
  editMode: boolean;
  richText?: boolean;
  blocks: Block[];
  setBlocks: React.Dispatch<React.SetStateAction<Block[]>>;
  updateBlock: (id: string, patch: Partial<Block>) => void;
  deleteBlock: (id: string) => void;
  insertBlock: (afterId: string | null, type: Block['type']) => void;
  moveBlock: (fromId: string, toId: string) => void;
  uploadFile: (file: File) => Promise<string>;
  focusedId: string | null;
  setFocusedId: (id: string | null) => void;
  canUndo: boolean;
  canRedo: boolean;
  undo: () => void;
  redo: () => void;
}

export const EditorContext = createContext<EditorCtx>(null as any);
export const useEditorCtx = () => useContext(EditorContext);

/* ── Read-only provider: lets BlockRenderer run outside the full editor ── */
const NOOP = () => {};
const NOOP_ASYNC = async () => '';
export function ReadOnlyEditorProvider({ blocks, children }: { blocks: Block[]; children: React.ReactNode }) {
  const ctx: EditorCtx = {
    editMode: false,
    blocks,
    setBlocks: NOOP as any,
    updateBlock: NOOP as any,
    deleteBlock: NOOP as any,
    insertBlock: NOOP as any,
    moveBlock: NOOP as any,
    uploadFile: NOOP_ASYNC,
    focusedId: null,
    setFocusedId: NOOP,
    canUndo: false,
    canRedo: false,
    undo: NOOP,
    redo: NOOP,
  };
  return <EditorContext.Provider value={ctx}>{children}</EditorContext.Provider>;
}

/* ═══════════════════════════════════════════════════════════
   Top bar
   ═══════════════════════════════════════════════════════════ */
function TopBar({
  title, dirty, saving, editMode, onToggleEdit, onSave, onSettings, canUndo, canRedo, onUndo, onRedo, backHref = '/admin', liveUrl,
}: {
  title: string;
  dirty: boolean;
  saving: boolean;
  editMode: boolean;
  onToggleEdit: () => void;
  onSave: () => void;
  onSettings: () => void;
  canUndo: boolean;
  canRedo: boolean;
  onUndo: () => void;
  onRedo: () => void;
  backHref?: string;
  liveUrl?: string;
}) {
  return (
    <div className="be-topbar">
      <Link href={backHref} className="be-topbar__back">
        <svg width="14" height="14" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24" aria-hidden="true">
          <path d="M19 12H5M12 19l-7-7 7-7" strokeLinecap="round" strokeLinejoin="round" />
        </svg>
        All posts
      </Link>
      <div className="be-topbar__divider" />
      <span className="be-topbar__title">{title || 'Untitled post'}</span>
      <div className="be-topbar__right">
        {/* Undo / Redo */}
        <div className="be-topbar__undo-group">
          <button
            className="be-undo-btn"
            onClick={onUndo}
            disabled={!canUndo}
            title="Undo (⌘Z)"
            aria-label="Undo"
          >
            <svg width="14" height="14" fill="currentColor" viewBox="0 0 256 256" aria-hidden="true"><path d="M224,128a96,96,0,0,1-96,96H72a8,8,0,0,1,0-16h56a80,80,0,1,0-72.69-46.56A8,8,0,1,1,41,170.43,96,96,0,1,1,224,128ZM59.31,92.69a8,8,0,0,0,11.32,11.32l-24-24a8,8,0,0,0-11.32,11.32l16,16A8,8,0,0,0,59.31,92.69ZM40,96H72a8,8,0,0,0,0-16H40a8,8,0,0,0,0,16Z"/></svg>
            <span>Undo</span>
            <kbd>⌘Z</kbd>
          </button>
          <div className="be-undo-sep" />
          <button
            className="be-undo-btn"
            onClick={onRedo}
            disabled={!canRedo}
            title="Redo (⌘⇧Z)"
            aria-label="Redo"
          >
            <svg width="14" height="14" fill="currentColor" viewBox="0 0 256 256" aria-hidden="true"><path d="M184,96H152a8,8,0,0,0,0,16h32a8,8,0,0,0,0-16ZM32,128a96,96,0,0,0,152.69,77.43A8,8,0,1,0,176,190.43,80,80,0,1,1,248.69,128,8,8,0,0,0,256,128a96,96,0,1,0-192,0Zm145.37-51.31-16,16a8,8,0,0,0,11.32,11.32l24-24a8,8,0,0,0-11.32-11.32Z"/></svg>
            <span>Redo</span>
            <kbd>⌘⇧Z</kbd>
          </button>
        </div>
        <div className="be-topbar__divider" />
        {dirty && <span className="be-topbar__save-dot" title="Unsaved changes" />}
        <button className={`be-topbar__btn be-topbar__btn--preview${editMode ? ' is-active' : ''}`} onClick={onToggleEdit}>
          {editMode ? (
            <>
              <svg width="13" height="13" fill="currentColor" viewBox="0 0 256 256" aria-hidden="true"><path d="M245.48,125.57C244.35,123.68,216.7,76,168,49.52,163.37,47,158.61,44.85,153.7,43L174.3,22.39a8,8,0,0,0-11.31-11.31L143,31.07a133.16,133.16,0,0,0-15-.95,134.67,134.67,0,0,0-75.87,23.26L32.5,34a8,8,0,0,0-11.32,11.32l19,19C14.3,95.38,10.78,123.38,10.52,125.57A8,8,0,0,0,18,134a7.91,7.91,0,0,0,1.49-.14,8,8,0,0,0,6.34-9.35C25.84,124.37,29,104.68,42.21,86L61,104.78A48,48,0,0,0,128,171l19.91,19.91C130.7,196.14,113.14,200,128,200c-38.24,0-74-16.06-101.47-42.86a8,8,0,0,0-11.06,11.55C45.31,198.3,84.71,216,128,216a155,155,0,0,0,32-3.24l18,18a8,8,0,0,0,11.31-11.31l-18.65-18.65c18.16-7.71,35-20.07,50.13-36.85,26.8-29.44,35.31-59.47,35.59-60.61A8,8,0,0,0,245.48,125.57ZM80,128a48.07,48.07,0,0,1,48-48,47.61,47.61,0,0,1,22.08,5.43L84.08,136.64A47.55,47.55,0,0,1,80,128Zm48,48a47.61,47.61,0,0,1-22.08-5.43l66-60.21A48,48,0,0,1,128,176Z"/></svg>
              Editing
            </>
          ) : (
            <>
              <svg width="13" height="13" fill="currentColor" viewBox="0 0 256 256" aria-hidden="true"><path d="M247.31,124.76c-.35-.79-8.82-19.58-27.65-38.41C194.57,61.26,162.88,48,128,48S61.43,61.26,36.34,86.35C17.51,105.18,9,124,8.69,124.76a8,8,0,0,0,0,6.5c.35.79,8.82,19.57,27.65,38.4C61.43,194.74,93.12,208,128,208s66.57-13.26,91.66-38.34c18.83-18.83,27.3-37.61,27.65-38.4A8,8,0,0,0,247.31,124.76ZM128,192c-30.78,0-57.67-11.19-79.93-33.25A133.47,133.47,0,0,1,25,128,133.33,133.33,0,0,1,48.07,97.25C70.33,75.19,97.22,64,128,64s57.67,11.19,79.93,33.25A133.46,133.46,0,0,1,231.05,128C223.84,141.46,192.43,192,128,192Zm0-112a48,48,0,1,0,48,48A48.05,48.05,0,0,0,128,80Zm0,80a32,32,0,1,1,32-32A32,32,0,0,1,128,160Z"/></svg>
              Preview
            </>
          )}
        </button>
        {liveUrl && (
          <a
            href={liveUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="be-topbar__btn be-topbar__btn--ghost"
            title="View on site"
            style={{ display: 'inline-flex', alignItems: 'center', gap: 4, textDecoration: 'none' }}
          >
            <svg width="13" height="13" fill="currentColor" viewBox="0 0 256 256" aria-hidden="true"><path d="M224,104a8,8,0,0,1-16,0V59.32l-82.34,82.34a8,8,0,0,1-11.32-11.32L196.68,48H152a8,8,0,0,1,0-16h64a8,8,0,0,1,8,8Zm-40,24a8,8,0,0,0-8,8v72H48V80h72a8,8,0,0,0,0-16H48A16,16,0,0,0,32,80V208a16,16,0,0,0,16,16H176a16,16,0,0,0,16-16V136A8,8,0,0,0,184,128Z"/></svg>
            View
          </a>
        )}
        <button className="be-topbar__btn be-topbar__btn--ghost" onClick={onSettings}>
          <svg width="13" height="13" fill="currentColor" viewBox="0 0 256 256" aria-hidden="true"><path d="M128,80a48,48,0,1,0,48,48A48.05,48.05,0,0,0,128,80Zm0,80a32,32,0,1,1,32-32A32,32,0,0,1,128,160Zm88-29.84q.06-2.16,0-4.32l14.92-18.64a8,8,0,0,0,1.48-7.06,107.21,107.21,0,0,0-10.88-26.25,8,8,0,0,0-6-3.93l-23.72-2.64q-1.48-1.56-3-3L186,40.54a8,8,0,0,0-3.94-6,107.71,107.71,0,0,0-26.25-10.87,8,8,0,0,0-7.06,1.49L130.16,40Q128,40,125.84,40L107.2,25.11a8,8,0,0,0-7.06-1.48A107.6,107.6,0,0,0,73.89,34.51a8,8,0,0,0-3.93,6L67.32,64.27q-1.56,1.49-3,3L40.54,70a8,8,0,0,0-6,3.94,107.71,107.71,0,0,0-10.87,26.25,8,8,0,0,0,1.49,7.06L40,125.84Q40,128,40,130.16L25.11,148.8a8,8,0,0,0-1.48,7.06,107.21,107.21,0,0,0,10.88,26.25,8,8,0,0,0,6,3.93l23.72,2.64q1.49,1.56,3,3L70,215.46a8,8,0,0,0,3.94,6,107.71,107.71,0,0,0,26.25,10.87,8,8,0,0,0,7.06-1.49L125.84,216q2.16.06,4.32,0l18.64,14.92a8,8,0,0,0,7.06,1.48,107.21,107.21,0,0,0,26.25-10.88,8,8,0,0,0,3.93-6l2.64-23.72q1.56-1.48,3-3L215.46,186a8,8,0,0,0,6-3.94,107.71,107.71,0,0,0,10.87-26.25,8,8,0,0,0-1.49-7.06Zm-16.1-6.5a73.93,73.93,0,0,1,0,7.72,8,8,0,0,0,1.74,5.36l14.19,17.73a91.57,91.57,0,0,1-6.23,15L187,173.24a8,8,0,0,0-5.1,2.64,74.11,74.11,0,0,1-5.46,5.46,8,8,0,0,0-2.64,5.1l-2.51,22.58a91.32,91.32,0,0,1-15,6.23l-17.74-14.19a8,8,0,0,0-5-1.75h-.48a73.93,73.93,0,0,1-7.72,0,8.06,8.06,0,0,0-5.36,1.74L100.45,215.8a91.57,91.57,0,0,1-15-6.23L82.76,187a8,8,0,0,0-2.64-5.1,74.11,74.11,0,0,1-5.46-5.46,8,8,0,0,0-5.1-2.64L47,171.28a91.32,91.32,0,0,1-6.23-15l14.19-17.74a8,8,0,0,0,1.74-5.36,73.93,73.93,0,0,1,0-7.72,8,8,0,0,0-1.74-5.36L40.8,102.38a91.57,91.57,0,0,1,6.23-15L69.76,82.76a8,8,0,0,0,5.1-2.64,74.11,74.11,0,0,1,5.46-5.46,8,8,0,0,0,2.64-5.1l2.51-22.58a91.32,91.32,0,0,1,15-6.23l17.74,14.19a8,8,0,0,0,5.36,1.74,73.93,73.93,0,0,1,7.72,0,8.06,8.06,0,0,0,5.36-1.74l17.73-14.19a91.57,91.57,0,0,1,15,6.23L173.24,69a8,8,0,0,0,5.1,2.64,74.11,74.11,0,0,1,5.46,5.46,8,8,0,0,0,5.1,2.64l22.58,2.51a91.32,91.32,0,0,1,6.23,15l-14.19,17.74A8,8,0,0,0,201.82,123.66Z"/></svg>
          Settings
        </button>
        <button
          className="be-topbar__btn be-topbar__btn--save"
          onClick={onSave}
          disabled={saving || !dirty}
        >
          {saving ? 'Saving…' : 'Save'}
        </button>
      </div>
    </div>
  );
}

/* ═══════════════════════════════════════════════════════════
   Insert zone between blocks
   ═══════════════════════════════════════════════════════════ */
function InsertZone({ afterId, onInsert }: { afterId: string | null; onInsert: (afterId: string | null, type: Block['type']) => void }) {
  const [open, setOpen] = useState(false);
  const btnRef = useRef<HTMLButtonElement>(null);

  return (
    <div className="be-insert-zone">
      <div className="be-insert-zone__line" />
      <button
        ref={btnRef}
        className="be-insert-zone__btn"
        aria-label="Insert block"
        onClick={() => setOpen(true)}
      >+</button>
      {open && (
        <BlockPickerPopup
          anchorEl={btnRef.current}
          onPick={(type) => { setOpen(false); onInsert(afterId, type); }}
          onClose={() => setOpen(false)}
        />
      )}
    </div>
  );
}

// Short label shown in the hover tag so an editor can tell which style/block
// a piece of text already uses (e.g. "H3" vs "P") before deciding whether to
// match it or change it.
const BLOCK_TYPE_LABEL: Record<Block['type'], string> = {
  paragraph: 'P', lede: 'Lead Paragraph', h2: 'H2', h3: 'H3', h4: 'H4', blockquote: 'Quote',
  pullquote: 'Pull Quote', ul: 'Bulleted List', ol: 'Numbered List', code: 'Code', hr: 'Divider',
  BlogImage: 'Image', ImageCarousel: 'Carousel', ImageGrid: 'Image Grid', BlogVideo: 'Video',
  Callout: 'Callout', ReadingCallout: 'Reading Callout', BlogTable: 'Table', Columns: 'Columns',
  AuthorCard: 'Author Card', InlineCTA: 'CTA', TLDR: 'TL;DR', StatsGrid: 'Stats Grid', rawHtml: 'Raw HTML',
};

/* ═══════════════════════════════════════════════════════════
   Block wrapper row (drag + controls)
   ═══════════════════════════════════════════════════════════ */
function BlockRow({ block, index }: { block: Block; index: number }) {
  const { editMode, blocks, deleteBlock, moveBlock, insertBlock, focusedId, setFocusedId } = useEditorCtx();
  const [dragOver, setDragOver] = useState(false);
  const [isDragging, setIsDragging] = useState(false);
  const rowRef = useRef<HTMLDivElement>(null);

  const handleDragStart = (e: React.DragEvent) => {
    e.dataTransfer.setData('text/block-id', block.id);
    e.dataTransfer.effectAllowed = 'move';
    setIsDragging(true);
  };
  const handleDragEnd = () => setIsDragging(false);
  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
    setDragOver(true);
  };
  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    const fromId = e.dataTransfer.getData('text/block-id');
    if (fromId && fromId !== block.id) moveBlock(fromId, block.id);
    setDragOver(false);
  };

  const isFocused = focusedId === block.id;
  const isFirst = index === 0;
  const isLast = index === blocks.length - 1;

  return (
    <div
      ref={rowRef}
      className={`be-block-row${isFocused ? ' is-focused' : ''}${dragOver ? ' is-drag-over' : ''}${isDragging ? ' is-dragging' : ''}`}
      onDragOver={handleDragOver}
      onDragLeave={() => setDragOver(false)}
      onDrop={handleDrop}
      onFocus={() => setFocusedId(block.id)}
      onBlur={(e) => { if (!rowRef.current?.contains(e.relatedTarget as Node)) setFocusedId(null); }}
      tabIndex={-1}
      style={{ outline: 'none' }}
    >
      {editMode && (
        <span className="be-block-type-tag" title={`This block is styled as: ${BLOCK_TYPE_LABEL[block.type] ?? block.type}`}>
          {BLOCK_TYPE_LABEL[block.type] ?? block.type}
        </span>
      )}
      {editMode && (
        <div className="be-block-controls">
          <button
            className="be-ctrl-btn be-ctrl-btn--drag"
            draggable
            onDragStart={handleDragStart}
            onDragEnd={handleDragEnd}
            aria-label="Drag to reorder"
            title="Drag to reorder"
          >
            <svg width="12" height="12" fill="currentColor" viewBox="0 0 256 256" aria-hidden="true"><path d="M108,60A16,16,0,1,1,92,44,16,16,0,0,1,108,60Zm56,0a16,16,0,1,1-16-16A16,16,0,0,1,164,60ZM92,112a16,16,0,1,0,16,16A16,16,0,0,0,92,112Zm72,0a16,16,0,1,0,16,16A16,16,0,0,0,164,112Zm-72,72a16,16,0,1,0,16,16A16,16,0,0,0,92,184Zm72,0a16,16,0,1,0,16,16A16,16,0,0,0,164,184Z"/></svg>
          </button>
          <button
            className="be-ctrl-btn be-ctrl-btn--move-up"
            onClick={() => !isFirst && moveBlock(block.id, blocks[index - 1].id)}
            disabled={isFirst}
            aria-label="Move block up"
            title="Move up"
          >
            <svg width="12" height="12" fill="currentColor" viewBox="0 0 256 256" aria-hidden="true"><path d="M208.49,120.49a12,12,0,0,1-17,0L140,69V216a12,12,0,0,1-24,0V69L64.49,120.49a12,12,0,0,1-17-17l72-72a12,12,0,0,1,17,0l72,72A12,12,0,0,1,208.49,120.49Z"/></svg>
          </button>
          <button
            className="be-ctrl-btn be-ctrl-btn--move-down"
            onClick={() => !isLast && moveBlock(block.id, blocks[index + 1].id)}
            disabled={isLast}
            aria-label="Move block down"
            title="Move down"
          >
            <svg width="12" height="12" fill="currentColor" viewBox="0 0 256 256" aria-hidden="true"><path d="M208.49,152.49l-72,72a12,12,0,0,1-17,0l-72-72a12,12,0,0,1,17-17L116,187V40a12,12,0,0,1,24,0V187l51.51-51.52a12,12,0,0,1,17,17Z"/></svg>
          </button>
          <button
            className="be-ctrl-btn be-ctrl-btn--delete"
            onClick={() => deleteBlock(block.id)}
            aria-label="Delete block"
            title="Delete block"
          >
            <svg width="13" height="13" fill="currentColor" viewBox="0 0 256 256" aria-hidden="true"><path d="M216,48H176V40a24,24,0,0,0-24-24H104A24,24,0,0,0,80,40v8H40a8,8,0,0,0,0,16h8V216a16,16,0,0,0,16,16H192a16,16,0,0,0,16-16V64h8a8,8,0,0,0,0-16ZM96,40a8,8,0,0,1,8-8h48a8,8,0,0,1,8,8v8H96Zm96,176H64V64H192ZM112,104v64a8,8,0,0,1-16,0V104a8,8,0,0,1,16,0Zm48,0v64a8,8,0,0,1-16,0V104a8,8,0,0,1,16,0Z"/></svg>
          </button>
        </div>
      )}
      <div className="be-block-inner">
        <BlockRenderer block={block} />
      </div>
    </div>
  );
}

/* ═══════════════════════════════════════════════════════════
   Editable article header (title, category, dek, authors, hero)
   ═══════════════════════════════════════════════════════════ */
function EditableHeader({
  meta,
  onMetaChange,
  fieldErrors,
  onClearError,
  isNewPost,
  onSlugChange,
  slugEditedRef,
}: {
  meta: PostMeta;
  onMetaChange: (patch: Partial<PostMeta>) => void;
  fieldErrors?: Record<string, string>;
  onClearError?: (key: string) => void;
  isNewPost?: boolean;
  onSlugChange?: (slug: string) => void;
  slugEditedRef?: { current: boolean };
}) {
  const { editMode, uploadFile } = useEditorCtx();
  const heroInputRef = useRef<HTMLInputElement>(null);
  // Newsroom is no longer manually selectable from this dropdown — once a
  // post is Newsroom (set only via the "+ New Newsroom" admin tab), show it
  // as a fixed, non-editable breadcrumb instead of a dropdown missing that
  // option (which would silently reclassify the post on the next save).
  const isNewsroomPost = toEditorial(meta.category || '', meta.contentType) === NEWSROOM_CATEGORY;

  const date = meta.date ? new Date(meta.date) : null;
  const formattedDate = date && !isNaN(date.getTime()) ? format(date, 'MMMM d, yyyy') : '';

  return (
    <>
      {/* Article header section */}
      <section className="section-x-pad" style={{ background: 'var(--seasons-color-surface-default)', paddingTop: 'var(--seasons-space-20)', paddingBottom: 'var(--seasons-space-16)' }}>
        <div className="blog-article-header">
          {/* Shown only while editing, only when an External Link is set (Settings
              panel). The card on listing pages will link straight to that URL —
              readers never see the body content below. */}
          {editMode && (meta.newsLink || '').trim() && (
            <div style={{
              maxWidth: 640, margin: '0 auto var(--seasons-space-6)',
              padding: 'var(--seasons-space-3) var(--seasons-space-4)',
              background: 'var(--seasons-color-warning-50, #fffbeb)',
              border: '1px solid var(--seasons-color-warning-200, #fde68a)',
              borderRadius: 'var(--seasons-radius-md)',
              fontSize: 'var(--seasons-font-size-13)',
              color: 'var(--seasons-color-warning-800, #92400e)',
              textAlign: 'center',
            }}>
              This post links straight to the URL in Settings → External Link — the content below won&apos;t be shown to readers.
            </div>
          )}

          {/* Breadcrumb — STORIES > Category (mirrors live page). Removed
              entirely for Newsroom, which doesn't live under /blogs. */}
          {!isNewsroomPost && (
            <div className="wbn-od-breadcrumb" style={{ justifyContent: 'center', marginBottom: 'var(--seasons-space-6)' }}>
              <Link href="/blogs" className="wbn-od-breadcrumb__link">Stories</Link>
              <svg width="12" height="12" viewBox="0 0 12 12" fill="none" aria-hidden="true" style={{ flexShrink: 0, color: 'var(--seasons-color-text-tertiary)' }}>
                <path d="M4.5 2.5L7.5 6l-3 3.5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
              </svg>
              {editMode ? (
                <>
                <select
                  className="wbn-od-breadcrumb__link"
                  style={{ border: fieldErrors?.category ? '1.5px solid #b91c1c' : '1.5px dashed var(--be-accent, #053bee)', background: 'transparent', cursor: 'pointer', fontFamily: 'inherit', fontSize: 'inherit', padding: '1px 6px', borderRadius: 4 }}
                  value={toEditorial(meta.category || '', meta.contentType) || ''}
                  onChange={(e) => {
                    const next = e.target.value;
                    if (next !== toEditorial(meta.category || '', meta.contentType)) {
                      onMetaChange({ contentType: next });
                    }
                    if (next) onClearError?.('category');
                  }}
                >
                  <option value="">— Content Type —</option>
                  <option value="Insights">Insights</option>
                  <option value="Customers">Customers</option>
                  <option value="Product">Product</option>
                  <option value="Conversations">Conversations</option>
                  <option value="Case Study">Case Study</option>
                </select>
                {fieldErrors?.category && (
                  <span style={{ color: 'var(--seasons-color-error-700, #b91c1c)', fontSize: 11, marginLeft: 6, fontFamily: 'var(--seasons-font-polysans, system-ui)' }}>
                    ✕ {fieldErrors.category}
                  </span>
                )}
                </>
              ) : (
                <Link
                  href={`/blogs?category=${toCategorySlug(meta.category || '', meta.contentType)}`}
                  className="wbn-od-breadcrumb__link"
                >
                  {toEditorial(meta.category || '', meta.contentType)}
                </Link>
              )}
            </div>
          )}

          <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
            {/* Edit-mode-only date picker (preview shows inline next to byline below) */}
            {editMode && (
              <div style={{ marginBottom: 'var(--seasons-space-4)' }}>
                <input
                  type="date"
                  className="be-inline-date"
                  value={meta.date ? meta.date.slice(0, 10) : ''}
                  onChange={(e) => onMetaChange({ date: e.target.value ? new Date(e.target.value + 'T12:00:00').toISOString() : '' })}
                  style={{ color: 'var(--seasons-color-prose-meta)', fontSize: 'var(--seasons-font-size-14)', border: '1.5px dashed var(--be-accent, #053bee)', borderRadius: 4, padding: '2px 6px', background: 'transparent', cursor: 'pointer', fontFamily: 'inherit' }}
                />
              </div>
            )}

            {/* Title — rich text (bold/italic/link/sup/sub via FormatToolbar).
                Stored as sanitized HTML; slug still derives from the plain
                text so URLs never contain markup. */}
            <h1
              className={`type-prose-display be-text-editable`}
              contentEditable={editMode || undefined}
              suppressContentEditableWarning
              data-placeholder="Article title"
              // No link option on the article's own title/heading.
              data-no-link
              style={{ color: 'var(--seasons-color-prose-heading)', maxWidth: 'none', marginBottom: fieldErrors?.title ? 'var(--seasons-space-2)' : 'var(--seasons-space-6)', textAlign: 'center', outline: 'none' }}
              onPaste={(e) => {
                e.preventDefault();
                const html = e.clipboardData.getData('text/html');
                const inserted = html
                  ? sanitizePastedHtml(html, false)
                  : e.clipboardData.getData('text/plain').replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/\r?\n/g, ' ');
                document.execCommand('insertHTML', false, inserted);
              }}
              onBlur={(e) => {
                const newTitle = e.currentTarget.innerHTML || '';
                const plainTitle = e.currentTarget.textContent || '';
                onMetaChange({ title: newTitle });
                if (isNewPost && !slugEditedRef?.current) {
                  onSlugChange?.(slugify(plainTitle));
                }
                if (plainTitle.trim()) onClearError?.('title');
              }}
              dangerouslySetInnerHTML={{ __html: meta.title || '' }}
            />
            {fieldErrors?.title && (
              <p style={{ color: 'var(--seasons-color-error-700, #b91c1c)', fontSize: 12, margin: '4px 0 var(--seasons-space-6)', fontFamily: 'var(--seasons-font-polysans, system-ui)', textAlign: 'center' }}>
                ✕ {fieldErrors.title}
              </p>
            )}

            {/* Authors — generic team names hidden from preview, same as live site */}
            {(meta.authors.length > 0 || editMode) && (
              <div className="blog-byline" style={{ fontSize: 'var(--seasons-font-size-16)' }}>
                <span className="blog-byline__by">By</span>
                {meta.authors.filter(a => editMode || !isGenericAuthor(a.name)).map((a, idx) => (
                  <span key={idx} className="blog-byline__author">
                    <span
                      className={`blog-author-link${editMode ? ' be-text-editable' : ''}`}
                      contentEditable={editMode || undefined}
                      suppressContentEditableWarning
                      data-placeholder="Author name"
                      style={{ outline: 'none', minWidth: editMode ? 60 : undefined }}
                      onBlur={(e) => {
                        const authors = [...meta.authors];
                        authors[idx] = { ...authors[idx], name: e.currentTarget.textContent || '' };
                        onMetaChange({ authors });
                      }}
                    >{a.name || null}</span>
                    {idx < meta.authors.length - 1 && (
                      <span className="blog-byline__sep">{idx === meta.authors.length - 2 ? ' and ' : ', '}</span>
                    )}
                  </span>
                ))}
                {editMode && (
                  <button
                    type="button"
                    style={{ marginLeft: 8, fontSize: 12, color: 'var(--be-accent, #053bee)', background: 'none', border: '1px dashed var(--be-accent, #053bee)', borderRadius: 4, padding: '1px 8px', cursor: 'pointer' }}
                    onClick={() => onMetaChange({ authors: [...meta.authors, { name: '', avatar: '', profileUrl: '' }] })}
                  >+ Author</button>
                )}
                {/* Date inline next to author with CalendarBlank icon (mirrors live page) */}
                {!editMode && formattedDate && (
                  <span style={{ color: 'var(--seasons-color-prose-meta)', marginLeft: 'var(--seasons-space-2)', display: 'inline-flex', alignItems: 'center', gap: '4px' }}>
                    · <CalendarBlank size={13} weight="regular" aria-hidden="true" />
                    <time dateTime={meta.date ? new Date(meta.date).toISOString().split('T')[0] : ''}>{formattedDate}</time>
                  </span>
                )}
              </div>
            )}
          </div>
        </div>
      </section>

      {/* Hero image */}
      <section className="section-x-pad" style={{ background: 'var(--seasons-color-surface-default)', paddingBottom: 'var(--seasons-space-12)' }}>
        <figure className="blog-hero-cover be-structured-block" style={{ position: 'relative' }}>
          {meta.heroImg ? (
            <div className="blog-hero-cover__media" style={{ position: 'relative' }}>
              <img src={meta.heroImg} alt={meta.heroAlt || meta.title || ''} style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }} />
              {editMode && (
                <>
                  <button className="be-structured-block__settings-btn" onClick={() => heroInputRef.current?.click()}>
                    <svg width="12" height="12" fill="currentColor" viewBox="0 0 256 256" aria-hidden="true"><path d="M213.66,82.34l-56-56a8,8,0,0,0-11.32,0l-96,96A8,8,0,0,0,48,128v56a8,8,0,0,0,8,8h56a8,8,0,0,0,5.66-2.34l96-96A8,8,0,0,0,213.66,82.34ZM108.69,176H64V131.31l72-72L180.69,104Zm84-84L147.31,47.31,168,26.62,202.69,61.31Z"/></svg>
                    Change image
                  </button>
                  <button
                    className="be-structured-block__settings-btn"
                    onClick={() => onMetaChange({ heroImg: '', heroAlt: '' })}
                    style={{ background: 'rgba(185,28,28,0.85)', marginLeft: 4 }}
                    title="Remove hero image"
                  >
                    <svg width="12" height="12" fill="currentColor" viewBox="0 0 256 256"><path d="M205.66,194.34a8,8,0,0,1-11.32,11.32L128,139.31,61.66,205.66a8,8,0,0,1-11.32-11.32L116.69,128,50.34,61.66A8,8,0,0,1,61.66,50.34L128,116.69l66.34-66.35a8,8,0,0,1,11.32,11.32L139.31,128Z"/></svg>
                    Remove
                  </button>
                </>
              )}
            </div>
          ) : editMode ? (
            <div
              className="be-upload-zone"
              style={{ aspectRatio: '16 / 9', borderRadius: 'var(--seasons-radius-md)' }}
              onClick={() => heroInputRef.current?.click()}
            >
              <svg width="32" height="32" fill="currentColor" viewBox="0 0 256 256" aria-hidden="true" style={{ opacity: 0.4 }}><path d="M213.66,82.34l-56-56A8,8,0,0,0,152,24H56A16,16,0,0,0,40,40V216a16,16,0,0,0,16,16H200a16,16,0,0,0,16-16V88A8,8,0,0,0,213.66,82.34ZM160,51.31,188.69,80H160ZM200,216H56V40h88V88a8,8,0,0,0,8,8h48V216Zm-40-68a8,8,0,0,1-8,8H136v8a8,8,0,0,1-16,0v-8H112a8,8,0,0,1,0-16h8v-8a8,8,0,0,1,16,0v8h8A8,8,0,0,1,160,148Z"/></svg>
              <span style={{ fontSize: 14, marginTop: 4 }}>Click to add hero image</span>
            </div>
          ) : null}
          <input
            ref={heroInputRef}
            type="file"
            accept="image/*"
            style={{ display: 'none' }}
            onChange={async (e) => {
              const f = e.target.files?.[0];
              if (!f) return;
              const url = await uploadFile(f);
              onMetaChange({ heroImg: url });
              e.target.value = '';
            }}
          />
        </figure>
      </section>
    </>
  );
}

/* ═══════════════════════════════════════════════════════════
   Listen + Share section (below hero, above body)
   ═══════════════════════════════════════════════════════════ */
function ListenShareSection({
  meta, editMode, onMetaChange, slug,
}: { meta: PostMeta; editMode: boolean; onMetaChange: (p: Partial<PostMeta>) => void; slug: string }) {
  const audioInputRef = useRef<HTMLInputElement>(null);

  // Normalise duration: treat blank / "0" / "00:00" all as the display default
  const duration = meta.audioDuration && meta.audioDuration !== '0' ? meta.audioDuration : '00:00';

  const PlayIcon = () => <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" fill="currentColor" viewBox="0 0 256 256"><path d="M240,128a15.74,15.74,0,0,1-7.6,13.51L88.32,229.65a16,16,0,0,1-16.2.3A15.86,15.86,0,0,1,64,216.13V39.87a15.86,15.86,0,0,1,8.12-13.82,16,16,0,0,1,16.2.3L232.4,114.49A15.74,15.74,0,0,1,240,128Z" /></svg>;

  return (
    <section className="section-x-pad" style={{ background: 'var(--seasons-color-surface-default)', borderTop: 'var(--seasons-border-width-default) solid var(--seasons-color-border-default)', paddingTop: 'var(--seasons-space-4)', paddingBottom: 'var(--seasons-space-8)' }}>
      <div className="blog-listen-section">
        <div className="blog-listen-row">
          {editMode ? (
            <>
              <button
                type="button"
                className="blog-listen-pill"
                aria-label={meta.audioUrl ? 'Change audio' : 'Upload audio'}
                onClick={() => audioInputRef.current?.click()}
                title="Click to change audio file"
              >
                <span className="blog-listen-pill__icon"><PlayIcon /></span>
                <span className="blog-listen-pill__label">
                  {meta.audioUrl ? 'Change audio' : 'Upload audio'}
                </span>
                <span className="blog-listen-pill__sep" aria-hidden="true" />
                <span
                  className="blog-listen-pill__time be-text-editable"
                  contentEditable
                  suppressContentEditableWarning
                  data-placeholder="00:00"
                  onBlur={(e) => onMetaChange({ audioDuration: e.currentTarget.textContent || '' })}
                  onClick={(e) => e.stopPropagation()}
                >{duration}</span>
              </button>
              <input ref={audioInputRef} type="file" accept="audio/*" style={{ display: 'none' }}
                onChange={async (e) => {
                  const f = e.target.files?.[0];
                  if (!f) return;
                  const fd = new FormData(); fd.append('file', f);
                  const r = await fetch('/api/upload', { method: 'POST', body: fd });
                  const d = await r.json();
                  onMetaChange({ audioUrl: d.url || '' });
                  e.target.value = '';
                }}
              />
              {meta.audioUrl && (
                <button
                  type="button"
                  style={{ fontSize: 11, color: 'var(--seasons-color-error-700, #b91c1c)', background: 'none', border: 'none', cursor: 'pointer', padding: '4px 8px', fontFamily: 'var(--seasons-font-polysans, system-ui)' }}
                  onClick={() => onMetaChange({ audioUrl: '', audioDuration: '' })}
                >
                  ✕ Remove audio
                </button>
              )}
            </>
          ) : (
            // Preview mode mirrors the live post page: BlogListenButtonAuto
            // shows the player when audio is ready, a "Audio generating…"
            // pill while a background generation job is in flight (so the
            // user knows audio is coming), or nothing when no job/audio
            // exists. Polling resolves automatically without page reload.
            <BlogListenButtonAuto
              slug={slug}
              audioUrl={meta.audioUrl || undefined}
              audioTimings={meta.audioTimings || undefined}
              targetSelector="article.blog-prose"
            />
          )}
          <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--seasons-space-3)' }}>
            <span className="wbn-share-label">Share</span>
            <BlogShareInline title={meta.title || ''} />
          </div>
        </div>
      </div>
    </section>
  );
}

/* ═══════════════════════════════════════════════════════════
   Toast
   ═══════════════════════════════════════════════════════════ */
function Toast({ message, onDone }: { message: string; onDone: () => void }) {
  useEffect(() => {
    const t = setTimeout(onDone, 2400);
    return () => clearTimeout(t);
  }, [onDone]);
  return <div className="be-toast">{message}</div>;
}

/* ═══════════════════════════════════════════════════════════
   Main BlogEditor
   ═══════════════════════════════════════════════════════════ */
interface BlogEditorProps {
  slug: string;
  initialMeta: PostMeta;
  initialBlocks: Block[];
  /** True when brand was pre-filled from the admin dashboard this post was
   * created from — locks the Brand dropdown in Settings so it can't be
   * accidentally changed away from that dashboard's brand. */
  brandLocked?: boolean;
}

/* History snapshot */
interface Snapshot { meta: PostMeta; blocks: Block[] }
const MAX_HISTORY = 50;

function slugify(s: string) {
  return s.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-+|-+$/g, '') || 'untitled';
}

function extractBlockText(blocks: Block[]): string {
  return blocks.map(b => {
    switch (b.type) {
      case 'paragraph': case 'lede': case 'h2': case 'h3': case 'h4': case 'blockquote': return b.text;
      case 'ul': case 'ol': return b.items.join(' ');
      case 'pullquote': return b.quote + (b.cite ? ' ' + b.cite : '');
      case 'Callout': return [b.title, b.children].filter(Boolean).join(' ');
      case 'TLDR': return b.body || '';
      case 'InlineCTA': return [b.heading, b.body].filter(Boolean).join(' ');
      case 'ReadingCallout': return b.body || '';
      case 'rawHtml': return (b.html || '').replace(/<[^>]+>/g, ' ').replace(/\s+/g, ' ').trim();
      default: return '';
    }
  }).filter(Boolean).join(' ').slice(0, 4000);
}

export default function BlogEditor({ slug, initialMeta, initialBlocks, brandLocked }: BlogEditorProps) {
  const router = useRouter();
  const pathname = usePathname();
  const { data: session } = useSession();
  const isPublisher = session?.user?.role === 'publisher';
  const isNewPost = pathname === '/editor/new';
  const editorBrand = (initialMeta as any).brand ?? '';
  const isFlowEditor   = editorBrand === 'flow';
  const isCometEditor  = editorBrand === 'comet';
  const isGalaxyEditor = editorBrand === 'galaxy';
  const isGravityEditor = editorBrand === 'gravity';
  const isStoryhealthEditor = editorBrand === 'storyhealth';
  const [currentSlug, setCurrentSlug] = useState(slug);
  // Shared across the main-canvas title heading AND the Settings panel's own
  // Title field — either one editing the title should auto-derive the slug
  // on a new post, until the slug itself has been manually touched. Lives
  // here (not inside FrontmatterPanel) so it survives Settings being closed
  // and reopened, and so both title-editing surfaces share one source of truth.
  const slugEditedRef = useRef(false);
  const [meta, setMeta] = useState<PostMeta>(initialMeta);
  const [blocks, setBlocks] = useState<Block[]>(initialBlocks);
  // Additional brands (beyond meta.brand) to also create this post for —
  // only meaningful while isNewPost; see save() below. Not part of PostMeta
  // since it's a one-time creation instruction, not something the saved
  // post itself should carry.
  const [extraBrands, setExtraBrands] = useState<string[]>([]);
  // Always-current refs for use in async save (avoids stale closure captures)
  const blocksRef = useRef(blocks);
  const metaRef = useRef(meta);
  const slugRef = useRef(currentSlug);
  blocksRef.current = blocks;
  metaRef.current = meta;
  slugRef.current = currentSlug;
  const [editMode, setEditMode] = useState(true);
  const [dirty, setDirty] = useState(false);
  const [saving, setSaving] = useState(false);
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [toast, setToast] = useState<string | null>(null);
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});
  const [focusedId, setFocusedId] = useState<string | null>(null);
  const [pickerOpen, setPickerOpen] = useState(false);
  const pickerBtnRef = useRef<HTMLButtonElement>(null);
  const progressRef = useRef<HTMLDivElement>(null);
  const backTopRef = useRef<HTMLButtonElement>(null);
  /* Scroll: progress bar + back-to-top */
  useEffect(() => {
    const onScroll = () => {
      const docHeight = document.body.scrollHeight - window.innerHeight;
      const ratio = docHeight > 0 ? window.scrollY / docHeight : 0;
      if (progressRef.current) progressRef.current.style.transform = `scaleX(${ratio})`;
      if (backTopRef.current) backTopRef.current.classList.toggle('blog-back-to-top--visible', window.scrollY > 400);
    };
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  /* History for undo/redo */
  const historyRef = useRef<Snapshot[]>([{ meta: initialMeta, blocks: initialBlocks }]);
  const historyIdxRef = useRef(0);
  const skipHistoryRef = useRef(false); // true when applying undo/redo

  const [canUndo, setCanUndo] = useState(false);
  const [canRedo, setCanRedo] = useState(false);

  const pushHistory = useCallback((snap: Snapshot) => {
    if (skipHistoryRef.current) return;
    const hist = historyRef.current;
    // Drop any future history when a new action is taken
    hist.splice(historyIdxRef.current + 1);
    hist.push(snap);
    if (hist.length > MAX_HISTORY) hist.shift();
    historyIdxRef.current = hist.length - 1;
    setCanUndo(historyIdxRef.current > 0);
    setCanRedo(false);
  }, []);

  const undo = useCallback(() => {
    if (historyIdxRef.current <= 0) return;
    historyIdxRef.current--;
    const snap = historyRef.current[historyIdxRef.current];
    skipHistoryRef.current = true;
    setMeta(snap.meta);
    setBlocks(snap.blocks);
    setDirty(true);
    skipHistoryRef.current = false;
    setCanUndo(historyIdxRef.current > 0);
    setCanRedo(true);
  }, []);

  const redo = useCallback(() => {
    if (historyIdxRef.current >= historyRef.current.length - 1) return;
    historyIdxRef.current++;
    const snap = historyRef.current[historyIdxRef.current];
    skipHistoryRef.current = true;
    setMeta(snap.meta);
    setBlocks(snap.blocks);
    setDirty(true);
    skipHistoryRef.current = false;
    setCanUndo(true);
    setCanRedo(historyIdxRef.current < historyRef.current.length - 1);
  }, []);

  const mark = useCallback(() => setDirty(true), []);

  // History snapshots are paired by (meta, blocks). When pushing a snapshot for
  // a change in one slice, the OTHER slice must be the current live value
  // (metaRef/blocksRef) — NOT a peek at the previous snapshot in the stack,
  // which can be stale or empty after undo/redo, corrupting future undo state.

  const updateMeta = useCallback((patch: Partial<PostMeta>) => {
    setMeta(prev => {
      const next = { ...prev, ...patch };
      pushHistory({ meta: next, blocks: blocksRef.current });
      return next;
    });
    mark();
  }, [mark, pushHistory]);

  const updateBlock = useCallback((id: string, patch: Partial<Block>) => {
    setBlocks(prev => {
      const next = prev.map(b => b.id === id ? { ...b, ...patch } as Block : b);
      pushHistory({ meta: metaRef.current, blocks: next });
      return next;
    });
    mark();
  }, [mark, pushHistory]);

  const deleteBlock = useCallback((id: string) => {
    setBlocks(prev => {
      const next = prev.filter(b => b.id !== id);
      pushHistory({ meta: metaRef.current, blocks: next });
      return next;
    });
    mark();
  }, [mark, pushHistory]);

  const insertBlock = useCallback((afterId: string | null, type: Block['type']) => {
    const nb = defaultBlock(type);
    setBlocks(prev => {
      let next: Block[];
      if (afterId === null) {
        next = [nb, ...prev];
      } else {
        const idx = prev.findIndex(b => b.id === afterId);
        // If afterId not found, append at the end rather than insert at start
        const insertAt = idx === -1 ? prev.length : idx + 1;
        const copy = [...prev];
        copy.splice(insertAt, 0, nb);
        next = copy;
      }
      pushHistory({ meta: metaRef.current, blocks: next });
      return next;
    });
    setFocusedId(nb.id);
    mark();
  }, [mark, pushHistory]);

  const moveBlock = useCallback((fromId: string, toId: string) => {
    setBlocks(prev => {
      const copy = [...prev];
      const fromIdx = copy.findIndex(b => b.id === fromId);
      const toIdx = copy.findIndex(b => b.id === toId);
      if (fromIdx === -1 || toIdx === -1) return prev;
      const [removed] = copy.splice(fromIdx, 1);
      copy.splice(toIdx, 0, removed);
      pushHistory({ meta: metaRef.current, blocks: copy });
      return copy;
    });
    mark();
  }, [mark, pushHistory]);

  const uploadFile = useCallback(async (file: File): Promise<string> => {
    const fd = new FormData();
    fd.append('file', file);
    const res = await fetch('/api/upload', { method: 'POST', body: fd });
    if (!res.ok) {
      const msg = await res.text().catch(() => res.statusText);
      throw new Error(`Upload failed (${res.status}): ${msg.slice(0, 200)}`);
    }
    const data = await res.json();
    if (!data.url) throw new Error('Upload returned no URL');
    return data.url;
  }, []);

  const save = useCallback(async () => {
    if (saving) return;
    const newErrors: Record<string, string> = {};
    const t = metaRef.current.title?.trim() ?? '';
    const cat = (metaRef.current.category || metaRef.current.contentType || '').trim();
    const isRes = ['Case Study', 'White Paper', 'Whitepaper', 'Industry Report', 'Brochure'].includes(cat);
    // A link-out card (External Link set in Settings) never renders an internal
    // detail page, so it doesn't need body content, its own hosted PDF, or a
    // topic category (there's no internal listing page to file it under).
    const hasExternalLink = Boolean((metaRef.current.newsLink || '').trim());
    if (!t) newErrors.title = 'Title is required';
    if (!hasExternalLink && !cat) newErrors.category = 'Category is required';
    if (toEditorial(metaRef.current.category || '', metaRef.current.contentType) === NEWSROOM_CATEGORY && !metaRef.current.newsroomType?.length) {
      newErrors.newsroomType = 'Newsroom Type is required — open Settings to set it';
    }
    if (!(slugRef.current || '').trim()) newErrors.slug = 'Slug is required — open Settings to set it';
    // Reuse the hero image as the meta image when in link-out mode, so the
    // required-meta-image check below doesn't force a redundant upload.
    if (hasExternalLink && !(metaRef.current.metaImage || '').trim() && metaRef.current.heroImg) {
      metaRef.current.metaImage = metaRef.current.heroImg;
    }
    const bodyText = extractBlockText(blocksRef.current).trim();
    if (isRes && !hasExternalLink && bodyText.length < 20) newErrors.content = 'Please add some content — this is the "About this resource" description shown on the post page';
    if (isRes && !hasExternalLink) {
      if (!(metaRef.current.pdfUrl || '').trim()) newErrors.pdf = 'PDF is required — open Settings to upload or paste a PDF URL';
    }
    if (!(metaRef.current.heroImg || '').trim()) newErrors.heroImg = 'Hero image is required — open Settings to upload one';
    if (!hasExternalLink && !(metaRef.current.metaImage || '').trim()) newErrors.metaImage = 'Meta image is required — open Settings → SEO & Meta to upload or paste one';
    if (!(metaRef.current.date || '').trim()) newErrors.date = 'Publish Date is required — open Settings to set it';
    const hasAuthor = (metaRef.current.authors || []).some((a: any) => (a?.name || '').trim().length > 0);
    if (!hasExternalLink && !hasAuthor) newErrors.author = 'At least one author is required — open Settings → Authors';
    if (Object.keys(newErrors).length > 0) {
      setFieldErrors(newErrors);
      setToast('Please fill in the required fields');
      return;
    }
    setFieldErrors({});
    // Flush any pending contentEditable text into React state before reading blocks
    const activeEl = document.activeElement;
    if (activeEl instanceof HTMLElement && activeEl.isContentEditable) {
      flushSync(() => { activeEl.blur(); });
    }
    setSaving(true);
    try {
      const res = await fetch('/api/post/write', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ slug: slugRef.current, meta: metaRef.current, blocks: blocksRef.current }),
      });
      if (!res.ok) {
        const err = await res.json();
        throw new Error(err.error || 'Save failed');
      }
      setDirty(false);

      // Extra brands checked in Settings (new post or existing — the
      // multi-select stays available either way so brands can be added or
      // removed later, not just at creation): write one independent copy
      // per brand too, suffixed so each gets its own file instead of
      // colliding with the primary post's slug. Each target slug is
      // deterministic and distinct, so unlike ReplicateButton these can run
      // in parallel — there's no shared "which copy number is free?" probe
      // to race on.
      const primaryBrand = metaRef.current.brand || 'innovaccer';
      const otherBrands = extraBrands.filter(b => b && b !== primaryBrand);
      let extraBrandFailures = 0;
      if (otherBrands.length > 0) {
        const extraResults = await Promise.allSettled(
          otherBrands.map(brand =>
            fetch('/api/post/write', {
              method: 'POST',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify({
                slug: `${slugRef.current}-${brand}`,
                meta: { ...metaRef.current, brand },
                blocks: blocksRef.current,
              }),
            }).then(r => { if (!r.ok) throw new Error(); }),
          ),
        );
        extraBrandFailures = extraResults.filter(r => r.status === 'rejected').length;
      }
      setToast(
        otherBrands.length === 0
          ? 'Saved successfully'
          : extraBrandFailures > 0
          ? `Saved — ${otherBrands.length - extraBrandFailures} of ${otherBrands.length} extra brand copies failed`
          : `Saved — created for ${otherBrands.length + 1} brands`
      );

      // Auto-generate meta title + description when not yet set.
      // For new posts we AWAIT generation before navigating — otherwise
      // router.replace below would unmount this component and mount a fresh
      // editor at /editor/<slug> that reads the file from disk before the
      // fire-and-forget /api/post/save patch below has landed. That left the
      // Settings panel showing empty Meta Title/Description on first load,
      // even though the public page (read later, once the patch had time to
      // complete) correctly showed the generated value — a stale-read race.
      const _needsTitle = !metaRef.current.metaTitle;
      const _needsDesc  = !metaRef.current.metaDescription;
      if (_needsTitle || _needsDesc) {
        const _slug  = slugRef.current;
        const _title = metaRef.current.title || '';
        const _text  = extractBlockText(blocksRef.current);
        // Each resolves true only if that field was actually generated +
        // patched to disk — both generate-meta-* routes correctly refuse
        // (400 "Insufficient content") when title+body combined is too
        // short to generate anything meaningful from, e.g. a near-empty
        // draft. Tracking real success avoids showing a false "auto-
        // generated" toast (and silently leaving Settings looking empty)
        // when generation was actually skipped.
        const requests: Promise<boolean>[] = [];
        if (_needsTitle) {
          requests.push(
            fetch('/api/generate-meta-title', {
              method: 'POST',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify({ title: _title, text: _text }),
            }).then(async r => {
              if (!r.ok) return false;
              const { metaTitle } = await r.json();
              if (!metaTitle) return false;
              setMeta(prev => ({ ...prev, metaTitle }));
              await fetch('/api/post/save', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ slug: _slug, patches: [{ kind: 'frontmatter', path: 'metaTitle', value: metaTitle }] }),
              });
              return true;
            }).catch(() => false)
          );
        }
        if (_needsDesc) {
          requests.push(
            fetch('/api/generate-meta-description', {
              method: 'POST',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify({ title: _title, text: _text }),
            }).then(async r => {
              if (!r.ok) return false;
              const { metaDescription } = await r.json();
              if (!metaDescription) return false;
              setMeta(prev => ({ ...prev, metaDescription }));
              await fetch('/api/post/save', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ slug: _slug, patches: [{ kind: 'frontmatter', path: 'metaDescription', value: metaDescription }] }),
              });
              return true;
            }).catch(() => false)
          );
        }
        if (isNewPost) {
          const results = await Promise.all(requests);
          if (results.some(Boolean)) setToast('SEO meta fields auto-generated');
        } else {
          Promise.all(requests).then(results => {
            if (results.some(Boolean)) setToast('SEO meta fields auto-generated');
          });
        }
      }

      // Bust the Next.js client-side router cache so /admin shows this post immediately
      router.refresh();
      // After first save of a new post, update the URL to the real slug
      if (isNewPost) {
        router.replace(`/editor/${slugRef.current}`);
      }
    } catch (e: any) {
      setToast(`Error: ${e.message}`);
    } finally {
      setSaving(false);
    }
  }, [saving, isNewPost, extraBrands]);

  // Keyboard shortcuts: Cmd+S save, Cmd+Z undo, Cmd+Y/Cmd+Shift+Z redo
  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key === 's') { e.preventDefault(); save(); }
      if ((e.metaKey || e.ctrlKey) && e.key === 'z' && !e.shiftKey) { e.preventDefault(); undo(); }
      if ((e.metaKey || e.ctrlKey) && (e.key === 'y' || (e.key === 'z' && e.shiftKey))) { e.preventDefault(); redo(); }
    };
    // Intercept browser-native undo/redo inside contentEditable elements so
    // they don't corrupt the DOM out of sync with the app's history stack.
    const beforeInputHandler = (e: Event) => {
      const ie = e as InputEvent;
      if (ie.inputType === 'historyUndo' || ie.inputType === 'historyRedo') {
        e.preventDefault();
      }
    };
    window.addEventListener('keydown', handler);
    window.addEventListener('beforeinput', beforeInputHandler);
    return () => {
      window.removeEventListener('keydown', handler);
      window.removeEventListener('beforeinput', beforeInputHandler);
    };
  }, [save, undo, redo]);

  // Warn before leaving with unsaved changes
  useEffect(() => {
    const handler = (e: BeforeUnloadEvent) => {
      if (dirty) { e.preventDefault(); e.returnValue = ''; }
    };
    window.addEventListener('beforeunload', handler);
    return () => window.removeEventListener('beforeunload', handler);
  }, [dirty]);

  const ctx: EditorCtx = {
    editMode, blocks, setBlocks, updateBlock, deleteBlock, insertBlock, moveBlock, uploadFile, focusedId, setFocusedId,
    canUndo, canRedo, undo, redo,
    richText: true,
  };

  return (
    <EditorContext.Provider value={ctx}>
      <div className={`be-shell${editMode ? ' be-edit-mode' : ''}`}>
        {/* Nav first so it sticks at the very top */}
        {isFlowEditor   ? <FlowNavBar />
         : isCometEditor  ? <CometNavBar />
         : isGalaxyEditor ? <GalaxyNavBar />
         : isGravityEditor ? <GravityNavBar />
         : isStoryhealthEditor ? <StoryhealthNavBar />
         : <NavBarMain ctaLabel="REQUEST A DEMO" ctaHref="https://innovaccer.com/demo" storiesHref="/blogs" />}
        {editMode && <FormatToolbar />}
        <TopBar
          title={meta.title}
          dirty={dirty}
          saving={saving}
          editMode={editMode}
          onToggleEdit={() => setEditMode(e => !e)}
          onSave={save}
          onSettings={() => setSettingsOpen(true)}
          canUndo={canUndo}
          canRedo={canRedo}
          onUndo={undo}
          onRedo={redo}
          backHref={(() => {
            const base = adminBasePathForBrand(meta.brand);
            const cat = meta.category ?? '';
            if (cat === 'Case Study') return `${base}?tab=Case+Studies`;
            if (cat === 'White Paper' || cat === 'Whitepaper') return `${base}?tab=White+Papers`;
            if (cat === 'Industry Report' || cat === 'Research Paper') return `${base}?tab=Industry+Reports`;
            if (cat === 'Brochure') return `${base}?tab=Brochures`;
            return `${base}?tab=Stories`;
          })()}
          liveUrl={isNewPost ? undefined : (() => {
            // Newsroom is set via Content Type, not Category — without this,
            // brandExternalUrl never sees "Newsroom" and both the branded and
            // fallback URLs default to the blogs path instead of news.
            const isNewsroom = meta.contentType === 'Newsroom' || meta.category === 'Newsroom';
            return brandExternalUrl(meta.brand, currentSlug, isNewsroom ? 'Newsroom' : meta.category)
              ?? (isNewsroom ? `/news/${currentSlug}` : `/blogs/${currentSlug}`);
          })()}
        />

        <div className="be-page-offset">
          <div className="blog-page">

            {/* Scroll progress bar */}
            <div className="blog-progress" aria-hidden="true">
              <div className="blog-progress__fill" ref={progressRef} style={{ transform: 'scaleX(0)' }} />
            </div>

            <main>
              {/* Editable article header + hero */}
              <EditableHeader
                meta={meta}
                onMetaChange={updateMeta}
                fieldErrors={fieldErrors}
                onClearError={(k) => setFieldErrors(prev => { const n = {...prev}; delete n[k]; return n; })}
                isNewPost={isNewPost}
                onSlugChange={setCurrentSlug}
                slugEditedRef={slugEditedRef}
              />

              {/* Listen + Share */}
              <ListenShareSection meta={meta} editMode={editMode} onMetaChange={updateMeta} slug={currentSlug} />

              {/* Block body */}
              <section className="section-x-pad" style={{ background: 'var(--seasons-color-surface-default)', paddingTop: 'var(--seasons-space-8)', paddingBottom: 'var(--seasons-space-20)' }}>
                <div className="blog-reading-layout">
                  <div className="blog-reading-layout__main">
                    <article className="blog-prose" id="blog-article-body" style={{ position: 'relative' }}>
                      <div className="be-block-list">
                        {fieldErrors.content && editMode && (
                          <div style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '10px 12px', marginBottom: 8, background: '#fef2f2', border: '1px solid #fca5a5', borderRadius: 6, color: '#b91c1c', fontSize: 13, fontFamily: 'var(--seasons-font-polysans, system-ui)' }}>
                            <svg width="14" height="14" fill="currentColor" viewBox="0 0 256 256"><path d="M236.8,188.09,149.35,36.22a24.76,24.76,0,0,0-42.7,0L19.2,188.09a23.51,23.51,0,0,0,0,23.72A24.35,24.35,0,0,0,40.55,224h174.9a24.35,24.35,0,0,0,21.33-12.19A23.51,23.51,0,0,0,236.8,188.09ZM120,104a8,8,0,0,1,16,0v40a8,8,0,0,1-16,0Zm8,88a12,12,0,1,1,12-12A12,12,0,0,1,128,192Z"/></svg>
                            {fieldErrors.content}
                          </div>
                        )}
                        {blocks.map((block, idx) => (
                          <React.Fragment key={block.id}>
                            {editMode && <InsertZone afterId={idx === 0 ? null : blocks[idx - 1].id} onInsert={insertBlock} />}
                            <BlockRow block={block} index={idx} />
                          </React.Fragment>
                        ))}
                        {editMode && <InsertZone afterId={blocks.length > 0 ? blocks[blocks.length - 1].id : null} onInsert={insertBlock} />}
                      </div>

                      {editMode && (
                        <div style={{ paddingTop: 8 }}>
                          <button
                            ref={pickerBtnRef}
                            className="be-add-block-end"
                            onClick={() => setPickerOpen(true)}
                          >
                            <span className="be-add-block-end__icon">+</span>
                            <span>Add a block</span>
                          </button>
                          {pickerOpen && (
                            <BlockPickerPopup
                              anchorEl={pickerBtnRef.current}
                              onPick={(type) => { setPickerOpen(false); insertBlock(blocks.length > 0 ? blocks[blocks.length - 1].id : null, type); }}
                              onClose={() => setPickerOpen(false)}
                            />
                          )}
                        </div>
                      )}
                    </article>

                    {(() => {
                      const tags = (meta.tags || []) as { tag?: string }[];
                      const filledTags = tags.filter(t => t.tag?.trim());
                      if (!editMode && filledTags.length === 0) return null;
                      return (
                        <div className="blog-article-tags">
                          <span className="blog-article-tags__label">Topics</span>
                          {filledTags.map((t) => (
                            editMode ? (
                              <span
                                key={t.tag}
                                className="blog-tag blog-tag--muted"
                                style={{ display: 'inline-flex', alignItems: 'center', gap: 4, paddingRight: 6 }}
                              >
                                {t.tag}
                                <button
                                  type="button"
                                  onClick={() => updateMeta({ tags: tags.filter(x => x.tag !== t.tag) })}
                                  style={{
                                    background: 'none', border: 'none', cursor: 'pointer', padding: 0,
                                    display: 'flex', alignItems: 'center', color: 'inherit', opacity: 0.7, lineHeight: 1,
                                  }}
                                  aria-label={`Remove ${t.tag}`}
                                >
                                  <svg width="10" height="10" fill="currentColor" viewBox="0 0 256 256">
                                    <path d="M205.66,194.34a8,8,0,0,1-11.32,11.32L128,139.31,61.66,205.66a8,8,0,0,1-11.32-11.32L116.69,128,50.34,61.66A8,8,0,0,1,61.66,50.34L128,116.69l66.34-66.35a8,8,0,0,1,11.32,11.32L139.31,128Z"/>
                                  </svg>
                                </button>
                              </span>
                            ) : (
                              <span key={t.tag} className="blog-tag blog-tag--muted">{t.tag}</span>
                            )
                          ))}
                          {editMode && (
                            <input
                              type="text"
                              placeholder="+ Add topic"
                              onKeyDown={(e) => {
                                if (e.key === 'Enter' || e.key === ',') {
                                  e.preventDefault();
                                  const val = (e.currentTarget.value || '').trim().replace(/,$/, '');
                                  if (val && !tags.some(t => t.tag === val)) {
                                    updateMeta({ tags: [...tags, { tag: val }] });
                                  }
                                  e.currentTarget.value = '';
                                }
                              }}
                              style={{
                                background: 'transparent',
                                border: 'none',
                                borderBottom: '1px dashed var(--seasons-color-border-strong)',
                                outline: 'none',
                                color: 'var(--seasons-color-text-secondary)',
                                fontSize: 12,
                                fontFamily: 'var(--seasons-font-polysans, system-ui)',
                                padding: '2px 4px',
                                minWidth: 80,
                                width: 100,
                              }}
                            />
                          )}
                        </div>
                      );
                    })()}
                  </div>
                </div>
              </section>

              {/* Newsletter — compact-card (editable heading + body in edit mode) */}
              <section
                id="newsletter"
                style={{
                  background: 'var(--seasons-color-surface-default)',
                  paddingTop: 0,
                  paddingBottom: 'var(--seasons-space-16)',
                }}
              >
                <div className="blog-newsletter-section">
                  <div className="blog-newsletter-inner">
                    <div style={{
                      background: 'var(--seasons-color-surface-subtle)',
                      border: 'var(--seasons-border-width-default) solid var(--seasons-color-border-default)',
                      borderRadius: 'var(--seasons-radius-card-lg)',
                      padding: 'var(--seasons-space-10)',
                    }}>
                      <h3
                        className="type-heading-xl"
                        style={{
                          marginTop: 'var(--seasons-space-2)',
                          maxWidth: '80%',
                          color: 'var(--seasons-color-prose-heading)',
                          fontWeight: 'var(--seasons-font-weight-light)',
                          textWrap: 'balance',
                        } as React.CSSProperties}
                      >
                        {meta.newsletter?.heading || 'Stay connected with Innovaccer'}
                      </h3>
                      <p style={{
                        marginTop: 'var(--seasons-space-3)',
                        maxWidth: '80%',
                        fontFamily: 'var(--seasons-font-polysans)',
                        fontSize: 'var(--seasons-font-size-16)',
                        lineHeight: 'var(--seasons-line-height-relaxed)',
                        color: 'var(--seasons-color-prose-body)',
                      }}>
                        {meta.newsletter?.body || 'Subscribe to receive the latest insights, updates, and stories from healthcare innovation.'}
                      </p>
                      <div style={{ marginTop: 'var(--seasons-space-6)', pointerEvents: 'none' }}>
                        <InputSubmit
                          id="be-newsletter-email"
                          label="Work email"
                          type="email"
                          value=""
                          onChange={() => {}}
                          submitLabel="Subscribe"
                        />
                      </div>
                      <p style={{
                        marginTop: 'var(--seasons-space-3)',
                        maxWidth: '80%',
                        fontFamily: 'var(--seasons-font-polysans)',
                        fontSize: 'var(--seasons-font-size-13)',
                        lineHeight: 'var(--seasons-line-height-relaxed)',
                        color: 'var(--seasons-color-text-tertiary)',
                      }}>
                        Please provide your email address if you'd like to receive our monthly newsletter. You can unsubscribe at any time.
                      </p>
                    </div>
                  </div>
                </div>
              </section>

            </main>

            {/* Mobile TOC pill — page-level, no desktop sidebar */}
            <BlogTOC articleId="blog-article-body" />

            {/* Back to top */}
            <button type="button" className="blog-back-to-top" ref={backTopRef} aria-label="Back to top" onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}>
              <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" fill="currentColor" viewBox="0 0 256 256"><path d="M208.49,120.49a12,12,0,0,1-17,0L140,69V216a12,12,0,0,1-24,0V69L64.49,120.49a12,12,0,0,1-17-17l72-72a12,12,0,0,1,17,0l72,72A12,12,0,0,1,208.49,120.49Z" /></svg>
            </button>

            {isFlowEditor    ? <FlowFooter />
             : isCometEditor  ? <CometFooter />
             : isGalaxyEditor ? <GalaxyFooter />
             : isGravityEditor ? <GravityFooter />
             : isStoryhealthEditor ? <StoryhealthFooter />
             : <Footer theme="light" />}
          </div>
        </div>

        {/* Frontmatter settings panel */}
        {settingsOpen && (
          <FrontmatterPanel
            meta={meta}
            onMetaChange={updateMeta}
            onClose={() => setSettingsOpen(false)}
            uploadFile={uploadFile}
            slug={currentSlug}
            onSlugChange={setCurrentSlug}
            slugEditedRef={slugEditedRef}
            isNewPost={isNewPost}
            isResource={['Case Study', 'White Paper', 'Whitepaper', 'Industry Report', 'Brochure'].includes(meta.category || '')}
            brandLocked={isNewPost && brandLocked}
            extraBrands={extraBrands}
            onExtraBrandsChange={setExtraBrands}
            fieldErrors={fieldErrors}
            onClearError={(k) => setFieldErrors(prev => { const n = {...prev}; delete n[k]; return n; })}
            onSave={save}
            saving={saving}
            hasErrors={Object.keys(fieldErrors).length > 0}
            publishToggle={!isNewPost && currentSlug && isPublisher ? (
              <PublishToggle
                slug={currentSlug}
                status={(meta.status as 'draft' | 'published') ?? 'draft'}
                audioUrl={meta.audioUrl}
                category={meta.category}
              />
            ) : undefined}
          />
        )}

        {/* Toast */}
        {toast && <Toast message={toast} onDone={() => setToast(null)} />}
      </div>
    </EditorContext.Provider>
  );
}
